import { SelectInput } from "components/Select";
import {
  useGetStoresOptionsQuery,
  useGetRegionalResponsibleQuery,
  useGetStoresResponsiblesOptionsQuery,
} from "@/services/store/select-options";
import { GroupsEnum } from "@/enum/groups";
import { getUserFromLocalStorage } from "@/utils/getUserFromLocalStorage";
import { isUserAdminInGroup } from "@/utils/user";
import { MultiValueTag } from "@/components/ui/MultiValueTag";
import { getSelectClasses } from "@/utils/filter-styles";
import { formatOptionLabel } from "@/utils/format-option-label";
import { STATUS_FILTER_OPTIONS } from "@/constants/status-filter-options";
import { useFilterState } from "../../../ducks/filter/hooks/filterState";
import { useFilterActions } from "../../../ducks/filter/hooks/actions";

type OptionType = { value: string | number; label: string };

export default function GridFilters() {
  const filterState = useFilterState();
  const { updateFilter } = useFilterActions();
  const { filters } = filterState || { filters: {} };

  const user = getUserFromLocalStorage();
  const isAdmin = isUserAdminInGroup(user, GroupsEnum.CONCILIACAO);

  const { data: storeOptions } = useGetStoresOptionsQuery({ isAdmin });
  const { data: regionOptions } = useGetRegionalResponsibleQuery();
  const { data: responsibleOptions } = useGetStoresResponsiblesOptionsQuery();

  const handleMultiChange = (key: string) => (options: unknown) => {
    const opts = options as OptionType[] | null;
    const value = !opts || opts.length === 0
      ? undefined
      : opts.map((o) => String(o.value)).join(",");

    updateFilter({ key, value });
    updateFilter({ key: "meta", value: { ...filters.meta, page: 1 } });
  };

  const getSelectedOptions = (
    value: string | undefined,
    options: OptionType[] | undefined
  ): OptionType[] => {
    if (!value || !options) return [];
    const values = value.split(",");
    return options.filter((opt) => values.includes(String(opt.value)));
  };

  const storeValue = getSelectedOptions(filters.storeCode, storeOptions);
  const regionalValue = getSelectedOptions(filters.regionalId, regionOptions);
  const responsibleValue = getSelectedOptions(filters.responsibleId, responsibleOptions);
  const statusValue = getSelectedOptions(filters.status, STATUS_FILTER_OPTIONS);

  return (
    <div className="flex gap-2 flex-wrap">
      <SelectInput
        options={storeOptions}
        placeholder="Selecionar loja"
        value={storeValue}
        onChange={handleMultiChange("storeCode")}
        className={`${getSelectClasses(storeValue.length > 0)} min-w-[180px]`}
        classNamePrefix="an-simple-select"
        isMulti
        isClearable
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        components={{ MultiValue: MultiValueTag }}
        formatOptionLabel={formatOptionLabel}
      />
      <SelectInput
        options={regionOptions}
        placeholder="Selecionar regional"
        value={regionalValue}
        onChange={handleMultiChange("regionalId")}
        className={`${getSelectClasses(regionalValue.length > 0)} min-w-[180px]`}
        classNamePrefix="an-simple-select"
        isMulti
        isClearable
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        components={{ MultiValue: MultiValueTag }}
        formatOptionLabel={formatOptionLabel}
      />
      <SelectInput
        options={STATUS_FILTER_OPTIONS}
        placeholder="Selecionar status"
        value={statusValue}
        onChange={handleMultiChange("status")}
        className={`${getSelectClasses(statusValue.length > 0)} min-w-[180px]`}
        classNamePrefix="an-simple-select"
        isMulti
        isClearable
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        components={{ MultiValue: MultiValueTag }}
        formatOptionLabel={formatOptionLabel}
      />
      {isAdmin && (
        <SelectInput
          options={responsibleOptions}
          placeholder="Selecionar analista"
          value={responsibleValue}
          onChange={handleMultiChange("responsibleId")}
          className={`${getSelectClasses(responsibleValue.length > 0)} min-w-[180px]`}
          classNamePrefix="an-simple-select"
          isMulti
          isClearable
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          components={{ MultiValue: MultiValueTag }}
          formatOptionLabel={formatOptionLabel}
        />
      )}
    </div>
  );
}
